import { defineStore } from 'pinia'
import {
  verifyBcConnection,
  disconnectBcIntegration,
  fetchBcClientID,
  type TBcConnectionIssue,
} from '@/globals/typescript/utils/businessCentral'

type TBcConnectionStatus = 'unknown' | 'connected' | 'disconnected' | 'loading' | 'error'

export const useBusinessCentralStore = defineStore('businessCentral', {
  state: () => ({
    status: 'unknown' as TBcConnectionStatus,
    issue: null as TBcConnectionIssue | null,
    issueDetail: '',
    error: null as string | null,
    // True only while an explicit re-check (checkConnectionStatus) is in flight. Kept separate
    // from `status` so a re-check on an already-connected lab doesn't flip `isConnected` false
    // mid-check — that flip is what caused the connection card to swap to the taller
    // "not connected" markup and back, i.e. the layout jump.
    isVerifying: false,
    clientID: '',
    // When the last real connection check finished, epoch ms — session-only, deliberately not
    // persisted. The settings page states when it last looked, and a seeded `status` read off
    // the account doc says nothing about when anyone actually asked BC. Null means "not checked
    // in this session", which is the honest answer right after a reload.
    lastCheckedAt: null as number | null,
  }),

  getters: {
    isConnected: (state) => state.status === 'connected',
    isLoading: (state) => state.status === 'loading',
    isInitializing: (state) => state.status === 'unknown',
  },

  actions: {
    async loadClientID(labAccountID: string): Promise<void> {
      if (this.clientID) return
      try {
        this.clientID = await fetchBcClientID(labAccountID)
      } catch {
        this.clientID = ''
      }
    },

    // "Verificer forbindelse" — the one place the app-only connection is actually exercised and
    // the persisted isConnected flag gets self-healed. Optionally checks unsaved form values
    // instead of the saved settings, so the user finds out before saving whether what they just
    // typed will work.
    async checkConnectionStatus(
      labAccountID: string,
      override?: { tenantID?: string; environmentName?: string; companyID?: string }
    ): Promise<boolean> {
      // A call with overrides is a "would these values work?" probe of something the lab has not
      // saved — the wizard deliberately probes partial values on its way through the steps. Its
      // answer belongs to the wizard (issue/issueDetail), never to the lab's own connection
      // status, which still describes the settings actually stored.
      const isProbe = override !== undefined
      this.isVerifying = true
      this.error = null
      try {
        const { connected, issue, detail } = await verifyBcConnection(labAccountID, override)
        if (!isProbe) this.status = connected ? 'connected' : 'disconnected'
        this.issue = issue
        this.issueDetail = detail
        // Returned, not read back from `status`: a probe leaves `status` alone, so its own answer
        // is the only thing that says whether the probed values worked.
        return connected
      } catch {
        if (!isProbe) this.status = 'disconnected'
        this.issue = null
        this.issueDetail = ''
        return false
      } finally {
        // A probe answers for values the lab has not saved, so it does not date the lab's own
        // connection status any more than it sets it.
        if (!isProbe) this.lastCheckedAt = Date.now()
        this.isVerifying = false
      }
    },

    // Seeds status from the static, already-persisted value returned by the settings load
    // (integration.invoicing.options.isConnected) — no network round-trip, so a status is on
    // screen before any BC call. The BC settings page follows it with one real
    // `checkConnectionStatus`, since a persisted flag says only what was true last time anyone
    // looked; everywhere else keeps the cheap seeded value.
    setConnected(isConnected: boolean): void {
      this.status = isConnected ? 'connected' : 'disconnected'
      this.issue = null
      this.issueDetail = ''
      this.error = null
    },

    resetStore(): void {
      this.status = 'unknown'
      this.issue = null
      this.issueDetail = ''
      this.error = null
      this.isVerifying = false
      this.lastCheckedAt = null
    },

    async disconnect(labAccountID: string): Promise<void> {
      this.status = 'loading'
      this.error = null
      try {
        await disconnectBcIntegration(labAccountID)
        this.status = 'disconnected'
        this.issue = null
        this.issueDetail = ''
      } catch (e) {
        this.status = 'error'
        this.error = e instanceof Error ? e.message : 'disconnect-failed'
      }
    },
  },
})
